<template>
  <Layout>
    <PageHeader :title="$t('module.esgDocumentUsers')" :items="breadcrumbs" />
    <div class="card">
      <div class="card-body">
        <div v-if="document" class="mb-3">
          <h5>{{ document.name }}</h5>
          <small class="text-muted"
            >{{ $t("label.version") }}: {{ document.version }}</small
          >
        </div>
        <div v-if="summary" class="d-flex flex-wrap mb-3">
          <b-badge variant="success" class="mr-2 mb-2 p-2"
            >{{ $t("label.esgSigned") }}: {{ summary.signed_users }}</b-badge
          >
          <b-badge variant="warning" class="mb-2 p-2"
            >{{ $t("label.esgPendingSign") }}:
            {{ summary.unsigned_users }}</b-badge
          >
        </div>
        <b-tabs v-model="activeTab" nav-class="mb-3">
          <b-tab :title="$t('label.esgSigned')" />
          <b-tab :title="$t('label.esgPendingSign')" />
        </b-tabs>
        <div class="row mb-3">
          <div class="col-lg-4 mb-2 mb-lg-0">
            <b-input-group>
              <b-form-input
                v-model.trim="searchText"
                type="search"
                :placeholder="$t('label.search')"
                @keyup.enter="applySearch"
              />
              <b-input-group-append>
                <b-button
                  variant="info"
                  :aria-label="$t('label.search')"
                  @click="applySearch"
                >
                  <i class="mdi mdi-magnify"></i>
                </b-button>
              </b-input-group-append>
            </b-input-group>
          </div>
          <div class="col-lg-8 d-flex justify-content-lg-end">
            <b-button variant="outline-secondary" :to="{ name: 'EsgDocument' }">
              <i class="mdi mdi-arrow-left mr-1"></i
              >{{ $t("module.esgDocument") }}
            </b-button>
          </div>
        </div>
        <div v-if="loading" class="d-flex justify-content-center p-5">
          <b-spinner variant="info" />
        </div>
        <b-alert v-else-if="loadError" show variant="danger">
          {{ $t("label.esgDocumentUsersLoadError") }}
          <b-button size="sm" variant="outline-danger" @click="loadUsers">
            {{ $t("label.retryPermissionDefinitions") }}
          </b-button>
        </b-alert>
        <template v-else>
          <b-table
            :items="filteredUsers"
            :fields="fields"
            :current-page="currentPage"
            :per-page="pageSize"
            primary-key="uuid"
            show-empty
            responsive
          ><template #cell(company)="{ item }"><CompanyLink :uuid="item.companyUUID" :name="item.company" /></template></b-table>
          <div
            v-if="filteredUsers.length"
            class="d-flex flex-wrap justify-content-between align-items-center mt-3"
          >
            <small class="text-muted">{{
              $tc("label.results", filteredUsers.length, [filteredUsers.length])
            }}</small>
            <b-pagination
              v-model="currentPage"
              :total-rows="filteredUsers.length"
              :per-page="pageSize"
              class="mb-0"
              @change="pageNavigation"
            />
          </div>
        </template>
      </div>
    </div>
  </Layout>
</template>

<script>
import Layout from "@/views/layouts/main";
import PageHeader from "@/components/page-header";
import i18n from "@/i18n";
import { esgDocumentService } from "@/services/api/esgDocument.service";

export default {
  name: "EsgDocumentUsers",
  page: { title: i18n.t("module.esgDocumentUsers") },
  components: { Layout, PageHeader },
  data() {
    return {
      searchText: "",
      activeTab: 0,
      currentPage: 1,
      pageSize: 15,
      appliedSearch: "",
      users: [],
      document: null,
      summary: null,
      loading: false,
      loadError: false,
      requestId: 0,
    };
  },
  computed: {
    signingStatus() {
      return ["signed", "pending"][this.activeTab] || "signed";
    },
    fields() {
      return [
        { key: "name", label: this.$t("label.name") },
        { key: "email", label: this.$t("label.email") },
        { key: "company", label: this.$t("label.company") },
        { key: "signedAt", label: this.$t("label.esgSignedAt") },
      ];
    },
    filteredUsers() {
      const search = this.appliedSearch.toLowerCase();
      return this.users.filter((user) =>
        [user.name, user.email, user.company].some((value) =>
          value.toLowerCase().includes(search),
        ),
      );
    },
    breadcrumbs() {
      return [
        { text: this.$t("module.administration"), href: "/administrations" },
        { text: this.$t("module.esgDocument"), href: "/esg-documents" },
        { text: this.$t("module.user"), active: true },
      ];
    },
  },
  watch: {
    activeTab() {
      this.currentPage = 1;
      this.loadUsers();
    },
    "$route.params.documentUUID"() {
      this.searchText = "";
      this.appliedSearch = "";
      this.currentPage = 1;
      this.document = null;
      this.summary = null;
      this.loadUsers();
    },
  },
  created() {
    this.loadUsers();
  },
  beforeDestroy() {
    this.requestId++;
  },
  methods: {
    async loadUsers() {
      const requestId = ++this.requestId;
      const isSigned = this.signingStatus === "signed";
      this.loading = true;
      this.loadError = false;
      this.users = [];
      try {
        const result = await esgDocumentService.getSigningStatuses(
          this.$route.params.documentUUID,
          isSigned,
        );
        const rows =
          result?.data?.[isSigned ? "signed_users" : "unsigned_users"];
        if (!result?.success || !Array.isArray(rows)) {
          throw new Error("Signing status request failed.");
        }
        if (requestId !== this.requestId) return;
        this.document = result.data.document || null;
        this.summary = result.data.summary || null;
        this.users = rows.map((user) => ({
          uuid: user.uuid,
          name:
            user.name ||
            user.full_name ||
            [user.first_name, user.last_name].filter(Boolean).join(" "),
          email: user.email || "",
          companyUUID: user.company?.uuid || null,
          company: user.company?.name || "—",
          signedAt: user.signed_at || "—",
        }));
      } catch (error) {
        if (requestId === this.requestId) {
          this.loadError = true;
        }
      } finally {
        if (requestId === this.requestId) {
          this.loading = false;
        }
      }
    },
    applySearch() {
      this.currentPage = 1;
      this.appliedSearch = this.searchText;
    },
    pageNavigation(pageNumber) {
      this.currentPage = pageNumber;
    },
  },
};
</script>
